import { useMemo } from "react";
import { validateDepartment } from "../modals/utils";

export type DepartmentTypeOptions = "custom" | "pre-configured";

interface ValidationResult {
  isValid: boolean;
  error: string | null;
}

/**
 * Custom hook to validate department input based on type selection
 *
 * @param departmentType - Whether the department is custom or pre-configured
 * @param customLabel - Label for custom department
 * @param preConfigured - Selected pre-configured department value
 * @param existingTypes - Array of existing department types (uppercase)
 * @param defaultTypes - Array of pre-configured department types (uppercase)
 * @returns Validation result with isValid flag and error message
 *
 * @example
 * ```typescript
 * const validation = useValidateDepartment(
 *   'custom',
 *   'Sales Department',
 *   '',
 *   ['SERVICE', 'PARTS'],
 *   ['SALES', 'SERVICE']
 * );
 * // validation.isValid = false
 * // validation.error = "Cannot use a pre-configured department name"
 * ```
 */
export const useValidateDepartment = (
  departmentType: DepartmentTypeOptions,
  customLabel: string,
  preConfigured: string,
  existingTypes: string[],
  defaultTypes: string[]
): ValidationResult => {
  return useMemo(() => {
    // Validate custom department
    if (departmentType === "custom") {
      // Empty label is invalid
      if (!customLabel || customLabel.trim().length === 0) {
        return { isValid: false, error: "Label is required" };
      }

      // Use the existing validateDepartment function
      const validation = validateDepartment(existingTypes, defaultTypes)(
        "",
        customLabel
      );

      // If validation was triggered and failed
      if (validation.triggered && !validation.validationState.isValid) {
        return {
          isValid: false,
          error: validation.validationState.message || "Invalid department name"
        };
      }

      // Validation passed
      return { isValid: true, error: null };
    }

    // Validate pre-configured department
    if (departmentType === "pre-configured") {
      if (!preConfigured) {
        return {
          isValid: false,
          error: "Please select a department"
        };
      }
      return { isValid: true, error: null };
    }

    // Default case (should not reach here)
    return { isValid: true, error: null };
  }, [departmentType, customLabel, preConfigured, existingTypes, defaultTypes]);
};
